import { TERMS_VERSION, routes, type Me, type TelegramAuthResponse } from '@spot/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/button';
import { track } from '../../lib/analytics';
import { useSession } from '../auth/session-gate';
import { LegalLink } from '../legal/legal-link';

/**
 * First-launch consent (T-007, D48): the app opens once the current Terms and Privacy Policy are
 * accepted. No birth date and no age check here. Shown again when `TERMS_VERSION` goes up.
 */
export function ConsentGate({ children }: { children: ReactNode }) {
	const { me } = useSession();
	if (me.onboardingCompleted) return <>{children}</>;
	return <ConsentScreen />;
}

function ConsentScreen() {
	const { t } = useTranslation();
	const { api, me } = useSession();
	const queryClient = useQueryClient();
	const repeat = me.termsVersion !== null;
	const [accepted, setAccepted] = useState(false);

	useEffect(() => {
		track('consent_shown', { repeat });
	}, [repeat]);

	const save = useMutation({
		mutationFn: () => api.call(routes.consent, { body: { termsVersion: TERMS_VERSION } }),
		onSuccess: (next: Me) => {
			track('consent_accepted', { repeat });
			queryClient.setQueryData<TelegramAuthResponse>(['session'], (session) =>
				session === undefined ? session : { ...session, user: next },
			);
		},
	});

	return (
		<div className="flex h-full flex-col overflow-y-auto px-6 pt-8 pb-8">
			<img src="/brand/logo.svg" alt="SPOT" className="mt-8 h-10 w-auto self-start" />
			<h1 className="text-title mt-6 font-bold">{t('consent.title')}</h1>
			<p className="text-body text-text-muted mt-2">{t('consent.body')}</p>
			<form
				className="mt-8 flex flex-1 flex-col gap-4"
				onSubmit={(event) => {
					event.preventDefault();
					if (accepted) save.mutate();
				}}
			>
				<label className="flex items-start gap-3">
					<input
						type="checkbox"
						checked={accepted}
						onChange={(event) => {
							setAccepted(event.target.checked);
						}}
						className="accent-primary mt-1 size-5 shrink-0"
					/>
					<span className="text-body text-text">
						<Trans
							i18nKey="consent.accept"
							components={{
								terms: <LegalLink doc="terms" locale={me.locale} />,
								privacy: <LegalLink doc="privacy" locale={me.locale} />,
							}}
						/>
					</span>
				</label>
				{save.isError && (
					<p role="alert" className="text-caption text-danger">
						{t('errors.generic_body')}
					</p>
				)}
				<Button type="submit" className="mt-auto" disabled={!accepted || save.isPending}>
					{save.isPending ? t('consent.saving') : t('consent.continue')}
				</Button>
			</form>
		</div>
	);
}
